/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited
 * Licensed under the Apache License, Version 2.0
 */
import { axiosInstance } from './axios';

export interface AGUIClient {
  id: string;
  space_id: string;
  bot_id: string;
  name: string;
  status: number;
  identity_mode: 'creator' | 'jwt_assertion' | 'token_exchange';
}

export interface AGUIAPIKey {
  id: string;
  api_key_prefix: string;
  name: string;
  bot_id: string;
  space_id: string;
  client_id: string;
  owner_user_id: string;
  status: number;
  expired_at: number;
  last_used_at: number;
  created_at: number;
  updated_at: number;
}

export interface AGUIExternalTenant {
  id: string;
  client_id: string;
  issuer: string;
  audience: string;
  jwks_uri: string;
  allowed_algorithms: Array<'RS256' | 'PS256' | 'ES256'>;
  created_at: number;
  updated_at: number;
}

interface APIResponse<T> {
  code: number;
  msg: string;
  data?: T;
}

class AGUIApiService {
  getClient(botId: string): Promise<APIResponse<AGUIClient>> {
    return axiosInstance.post<unknown, APIResponse<AGUIClient>>(
      '/api/agui/client/get',
      { bot_id: botId },
    );
  }

  listAPIKeys(
    botId: string,
    scope: 'mine' | 'all' = 'mine',
  ): Promise<APIResponse<{ api_keys: AGUIAPIKey[] }>> {
    return axiosInstance.post<unknown, APIResponse<{ api_keys: AGUIAPIKey[] }>>(
      '/api/agui/api_key/list',
      {
        bot_id: botId,
        scope,
      },
    );
  }

  createAPIKey(input: {
    name: string;
    bot_id: string;
    space_id: string;
    client_id: string;
  }): Promise<APIResponse<{ api_key: AGUIAPIKey; raw_api_key: string }>> {
    return axiosInstance.post<
      unknown,
      APIResponse<{ api_key: AGUIAPIKey; raw_api_key: string }>
    >('/api/agui/api_key/create', input);
  }

  rotateAPIKey(input: {
    key_id: string;
    bot_id: string;
    overlap_seconds?: number;
  }): Promise<APIResponse<{ api_key: AGUIAPIKey; raw_api_key: string }>> {
    return axiosInstance.post<
      unknown,
      APIResponse<{ api_key: AGUIAPIKey; raw_api_key: string }>
    >('/api/agui/api_key/rotate', input);
  }

  revokeAPIKey(input: {
    key_id: string;
    bot_id: string;
  }): Promise<APIResponse<void>> {
    return axiosInstance.post<unknown, APIResponse<void>>(
      '/api/agui/api_key/revoke',
      input,
    );
  }

  registerExternalTenant(input: {
    bot_id: string;
    client_id: string;
    issuer: string;
    audience: string;
    jwks_uri: string;
    tenant_id: string;
    allowed_algorithms: Array<'RS256' | 'PS256' | 'ES256'>;
    identity_mode: 'jwt_assertion' | 'token_exchange';
  }): Promise<APIResponse<AGUIExternalTenant>> {
    return axiosInstance.post<unknown, APIResponse<AGUIExternalTenant>>(
      '/api/agui/client/external_tenant/register',
      input,
    );
  }
}

export const aguiApi = new AGUIApiService();
